import { useEffect } from "react";
import { Box, Alert } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import {
  fetchSourcingInsights,
  fetchSourcingDashboard,
} from "../../../store/contractDashboardSlice";
import Sidebar from "../../../components/Sidebar";
import SourcingPREfficiencySection from "../../../components/ContractDashboard/SourcingPREfficiencySection";
import InsightsCard from "../../../components/ContractDashboard/InsightsCard";
import MetricsCard from "../../../components/ContractDashboard/MetricsCard";
import SavingsRealizationRate from "../../../components/ContractDashboard/SavingsRealizationRate";
import SourcingCycleTime from "../../../components/ContractDashboard/SourcingCycleTime";
import CompetitiveIntensity from "../../../components/ContractDashboard/CompetitiveIntensity";
import SpendUnderManagement from "../../../components/ContractDashboard/SpendUnderManagement";
import ShouldCostCoverage from "../../../components/ContractDashboard/ShouldCostCoverage";
import PRVolumeDelays from "../../../components/ContractDashboard/PRVolumeDelays";
import PRCycleTimeTrend from "../../../components/ContractDashboard/PRCycleTimeTrend";
import FirstTimeRightPRRate from "../../../components/ContractDashboard/FirstTimeRightPRRate";
import TouchlessPRRate from "../../../components/ContractDashboard/TouchlessPRRate";
import PRComplianceRate from "../../../components/ContractDashboard/PRComplianceRate";
import TouchlessPRNPS from "../../../components/ContractDashboard/TouchlessPRNPS";
import PageLoader from "../../../components/PageLoader";
import styles from "./PrEfficiency.module.scss";
import DashboardFilters from "../../../components/ContractDashboard/DashboardFilters";

const PrEfficiency = () => {
  const dispatch = useAppDispatch();
  const {
    sourcingInsights,
    sourcingDashboard,
    sourcingInsightsLoading,
    sourcingDashboardLoading,
    error,
  } = useSelector((state: RootState) => state.contractDashboard);
  const { contractDashboardFilters } = useSelector(
    (state: RootState) => state.filters,
  );

  useEffect(() => {
    const filters = {
      group_of_companies: contractDashboardFilters.groupCompany,
      contract_type: contractDashboardFilters.contractType,
    };

    dispatch(fetchSourcingInsights(filters));
    dispatch(fetchSourcingDashboard(filters));
  }, [
    dispatch,
    contractDashboardFilters.groupCompany,
    contractDashboardFilters.contractType,
  ]);

  // Show loader until all APIs complete successfully
  const isLoading =
    (sourcingInsightsLoading || sourcingDashboardLoading) &&
    (!sourcingInsights || !sourcingDashboard);

  if (isLoading) {
    return <PageLoader label="Loading PR efficiency..." />;
  }

  if (error) {
    return (
      <Box className={styles.prEfficiencyContainer}>
        <Box className={styles.contentWrapper}>
          <Sidebar activeNav="dashboard" />
          <Box className={styles.mainContent}>
            <Alert severity="error" sx={{ m: 2 }}>
              {error}
            </Alert>
          </Box>
        </Box>
      </Box>
    );
  }

  if (!sourcingInsights || !sourcingDashboard || !sourcingDashboard.kpis) {
    return null;
  }

  // Extract insights array
  const insightsArray = sourcingInsights.insights || [];

  // Map API insight type to InsightsCard type
  const getCardType = (
    insightType: string,
  ): "risk" | "leakage" | "bottleneck" => {
    const typeMap: Record<string, "risk" | "leakage" | "bottleneck"> = {
      warning: "risk",
      info: "leakage",
      success: "bottleneck",
    };
    return typeMap[insightType] || "risk";
  };

  return (
    <Box className={styles.prEfficiencyContainer}>
      <Box className={styles.contentWrapper}>
        <Sidebar activeNav="dashboard" />
        <Box className={styles.mainContent}>
          <Box className={styles.dashboardContainer}>
            <DashboardFilters
              title="Sourcing & PR Efficiency"
              subtitle="Speed, competitiveness, and effectiveness of sourcing and PR processes."
            />

            {insightsArray.length > 0 && (
              <Box className={styles.insightsSection}>
                <Box className={styles.insightsGrid}>
                  {insightsArray.map((insight, index) => (
                    <InsightsCard
                      key={`${insight.title}-${index}`}
                      type={getCardType(insight.type)}
                      title={insight.title}
                      description={insight.message}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Box className={styles.metricsSection}>
              <Box className={styles.metricsGrid}>
                <MetricsCard
                  title="Savings Realization Rate"
                  value={`${sourcingDashboard.kpis.savingsRealizationRate.value}%`}
                  percentage={
                    sourcingDashboard.kpis.savingsRealizationRate.change
                  }
                  percentageLabel="vs previous quarter"
                />
                <MetricsCard
                  title="Avg Sourcing Cycle Time"
                  value={`${sourcingDashboard.kpis.avgSourcingCycleTime.value.toString()} ${sourcingDashboard.kpis.avgSourcingCycleTime.unit}`}
                  // amount={sourcingDashboard.kpis.avgSourcingCycleTime.unit}
                  percentage={
                    sourcingDashboard.kpis.avgSourcingCycleTime.change
                  }
                  percentageLabel="vs previous quarter"
                />
                <MetricsCard
                  title="Competitive Intensity"
                  value={`${sourcingDashboard.kpis.competitiveIntensity.value.toString()} ${sourcingDashboard.kpis.competitiveIntensity.unit}`}
                  // amount={sourcingDashboard.kpis.competitiveIntensity.unit}
                  percentage={
                    sourcingDashboard.kpis.competitiveIntensity.change
                  }
                  percentageLabel="vs previous quarter"
                />
                <MetricsCard
                  title="Spend Under Management"
                  value={`${sourcingDashboard.kpis.spendUnderManagement.value}%`}
                  percentage={
                    sourcingDashboard.kpis.spendUnderManagement.change
                  }
                  percentageLabel="vs previous quarter"
                />
              </Box>
            </Box>

            <Box className={styles.chartsSection}>
              <SavingsRealizationRate
                data={
                  sourcingDashboard.charts?.savingsRealizationTrend?.data || []
                }
              />
              <SourcingCycleTime
                data={sourcingDashboard.charts?.sourcingCycleTime?.data || []}
              />
            </Box>

            <Box className={styles.chartsSection}>
              <CompetitiveIntensity
                data={sourcingDashboard.competitiveIntensity?.data || []}
              />
              <SpendUnderManagement
                data={{
                  underContractPct:
                    sourcingDashboard.spendUnderManagement
                      ?.formallySourcedPct || 0,
                  notCoveredPct:
                    sourcingDashboard.spendUnderManagement?.notSourcedPct || 0,
                }}
              />
            </Box>

            <Box sx={{ width: "100%", marginBottom: "24px" }}>
              <ShouldCostCoverage
                data={sourcingDashboard.shouldCostBenchmarkCoverage?.data || []}
              />
            </Box>

            <Box className={styles.sectionTitle}>
              Focus: Efficiency, Compliance, User Experience
            </Box>

            <Box className={styles.metricsSection}>
              <Box className={styles.metricsGrid}>
                <MetricsCard
                  title="Total PRs"
                  value={sourcingDashboard.focus_kpis.totalPRs.value.toLocaleString()}
                  percentage={sourcingDashboard.focus_kpis.totalPRs.change}
                  percentageLabel="vs previous quarter"
                />
                <MetricsCard
                  title="PR Efficiency"
                  value={`${sourcingDashboard.focus_kpis.prEfficiency.value}%`}
                  percentage={sourcingDashboard.focus_kpis.prEfficiency.change}
                  percentageLabel="vs previous quarter"
                />
                <MetricsCard
                  title="Delayed PRs"
                  value={sourcingDashboard.focus_kpis.delayedPRs.value.toLocaleString()}
                  percentage={sourcingDashboard.focus_kpis.delayedPRs.change}
                  percentageLabel="vs previous quarter"
                />
                <MetricsCard
                  title="Avg Cycle Time"
                  value={`${sourcingDashboard.focus_kpis.avgCycleTime.value.toString()} ${sourcingDashboard.focus_kpis.avgCycleTime.unit}`}
                  // amount={}
                  percentage={sourcingDashboard.focus_kpis.avgCycleTime.change}
                  percentageLabel="vs previous quarter"
                />
              </Box>
            </Box>

            <Box className={styles.chartsSection}>
              <PRVolumeDelays
                data={sourcingDashboard.prVolumeAndDelays?.data || []}
              />
              <PRCycleTimeTrend
                data={sourcingDashboard.prCycleTimeTrend?.data || []}
              />
            </Box>

            <Box className={styles.chartsSection}>
              <FirstTimeRightPRRate
                data={sourcingDashboard.firstTimeRightRate?.data || []}
              />
              <TouchlessPRRate
                data={sourcingDashboard.touchlessPRRate?.breakdown || []}
              />
            </Box>

            <Box className={styles.chartsSection}>
              <PRComplianceRate
                data={sourcingDashboard.prComplianceRate?.data || []}
              />
              <TouchlessPRNPS data={sourcingDashboard.touchlessPrRateScore} />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PrEfficiency;
